import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMatches } from "@/features/matches/useMatches";
import { errorMessage } from "@/lib/api";
import {
  buildCoachFeedbackBoard,
  type CoachFeedbackBoardAction,
  type CoachFeedbackBoardCard,
  type CoachFeedbackBoardCardTone,
  type CoachFeedbackBoardLane,
} from "./coachFeedbackBoard";
import { PageHeader } from "./components/PageHeader";
import { useCoachEngagement } from "./hooks/useCoachEngagement";
import { useCoachGoalsList } from "./hooks/useCoachGoalsList";
import { useCoachSummariesList } from "./hooks/useCoachSummariesList";
import { useRoster } from "./hooks/useRoster";

/**
 * Coach Feedback Board — Phase 1 of the player-first coach
 * workspace. Bare `/coach` lands here, while the old Roster remains
 * available at `?tab=roster`.
 *
 * The board is intentionally a quiet triage surface, not a project
 * management tool: it answers "who needs attention?" and then links
 * into existing surfaces until the Player Feedback Home lands.
 */
export function CoachFeedbackBoardPage({
  onGiveFeedback,
}: {
  onGiveFeedback: () => void;
}) {
  const navigate = useNavigate({ from: "/coach" });
  const rosterQuery = useRoster();
  const engagementQuery = useCoachEngagement({});
  const goalsQuery = useCoachGoalsList();
  const matchesQuery = useMatches();
  const summariesQuery = useCoachSummariesList();

  const board = useMemo(
    () =>
      buildCoachFeedbackBoard({
        players: rosterQuery.data?.players ?? [],
        engagement: engagementQuery.data,
        goals: goalsQuery.data?.goals ?? [],
        matches: matchesQuery.data ?? [],
        summaries: summariesQuery.data?.summaries ?? [],
      }),
    [
      engagementQuery.data,
      goalsQuery.data?.goals,
      matchesQuery.data,
      rosterQuery.data?.players,
      summariesQuery.data?.summaries,
    ],
  );

  const isLoading =
    rosterQuery.isLoading ||
    engagementQuery.isLoading ||
    goalsQuery.isLoading ||
    matchesQuery.isLoading ||
    summariesQuery.isLoading;
  const errors = [
    rosterQuery.error,
    engagementQuery.error,
    goalsQuery.error,
    matchesQuery.error,
    summariesQuery.error,
  ].filter(Boolean);

  const goToAction = (action: CoachFeedbackBoardAction) => {
    if (action.kind === "roster") {
      void navigate({
        to: "/coach",
        search: {
          tab: "roster",
          player_id: action.playerId,
          context: action.context,
        },
      });
      return;
    }
    if (action.kind === "engagement") {
      void navigate({ to: "/coach", search: { tab: "engagement" } });
      return;
    }
    if (action.kind === "notes") {
      void navigate({ to: "/coach", search: { tab: "notes" } });
      return;
    }
    if (action.kind === "playlists") {
      void navigate({ to: "/coach", search: { tab: "playlists" } });
      return;
    }
    void navigate({ to: "/coach", search: { tab: "summaries" } });
  };

  return (
    <div className="flex flex-col gap-5" data-testid="coach-feedback-board">
      <PageHeader
        title="Squad Feedback Board"
        description="A quiet triage view for player follow-up, review completion, and match-level coaching gaps."
        actions={
          <>
            <button
              type="button"
              onClick={() =>
                void navigate({ to: "/coach", search: { tab: "engagement" } })
              }
              className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink transition-colors hover:bg-surface"
            >
              View follow-up
            </button>
            <button
              type="button"
              onClick={onGiveFeedback}
              className="rounded bg-accent px-3 py-1.5 text-xs font-display uppercase tracking-wider text-white transition-colors hover:bg-accent-hover"
            >
              Give feedback
            </button>
          </>
        }
      />

      <section
        aria-label="Feedback attention summary"
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="coach-feedback-board-summary"
      >
        <SummaryTile label="Needs touch" value={board.summary.needsTouch} />
        <SummaryTile label="Follow-up" value={board.summary.followUp} />
        <SummaryTile label="Unreviewed" value={board.summary.unreviewed} />
        <SummaryTile label="Drafting" value={board.summary.missingRecaps} />
      </section>

      {errors.length > 0 ? (
        <div
          className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-ink"
          data-testid="coach-feedback-board-error"
        >
          Some board data could not load:{" "}
          <span className="text-ink-muted">
            {errors.map((err) => errorMessage(err)).join(" · ")}
          </span>
        </div>
      ) : null}

      {isLoading ? (
        <p
          className="px-3 py-6 font-mono text-sm text-ink-muted"
          data-testid="coach-feedback-board-loading"
        >
          Loading feedback board…
        </p>
      ) : (
        <section
          aria-label="Feedback attention lanes"
          className="grid gap-3 xl:grid-cols-4"
          data-testid="coach-feedback-board-lanes"
        >
          {board.lanes.map((lane) => (
            <BoardLane key={lane.id} lane={lane} onAction={goToAction} />
          ))}
        </section>
      )}
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-rule bg-bg-card px-3 py-3">
      <div className="font-display text-2xl text-ink">{value}</div>
      <div className="mt-1 text-xs font-display uppercase tracking-wider text-ink-muted">
        {label}
      </div>
    </div>
  );
}

function BoardLane({
  lane,
  onAction,
}: {
  lane: CoachFeedbackBoardLane;
  onAction: (action: CoachFeedbackBoardAction) => void;
}) {
  return (
    <section
      className="min-w-0 rounded-lg border border-rule bg-bg-card"
      data-testid={`coach-feedback-board-lane-${lane.id}`}
    >
      <header className="flex items-start justify-between gap-3 border-b border-rule px-3 py-3">
        <div className="min-w-0">
          <h3 className="text-base font-display text-ink">{lane.title}</h3>
          <p className="mt-1 text-xs text-ink-muted">{lane.description}</p>
        </div>
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-surface font-mono text-xs text-ink">
          {lane.cards.length}
        </span>
      </header>
      <div className="flex flex-col gap-2 p-2">
        {lane.cards.length === 0 ? (
          <p className="rounded border border-dashed border-rule bg-surface/60 px-3 py-4 text-sm text-ink-muted">
            Nothing needs attention here right now.
          </p>
        ) : (
          lane.cards.map((card) => (
            <BoardCard key={card.id} card={card} onAction={onAction} />
          ))
        )}
      </div>
    </section>
  );
}

function BoardCard({
  card,
  onAction,
}: {
  card: CoachFeedbackBoardCard;
  onAction: (action: CoachFeedbackBoardAction) => void;
}) {
  return (
    <article
      className={[
        "rounded-md border bg-surface px-3 py-3 shadow-sm",
        toneClass(card.tone),
      ].join(" ")}
      data-testid="coach-feedback-board-card"
      data-board-card-id={card.id}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="truncate text-sm font-display text-ink">
            {card.title}
          </h4>
          <p className="mt-0.5 truncate font-mono text-[0.68rem] uppercase tracking-wider text-ink-muted">
            {card.meta}
          </p>
        </div>
      </div>
      <p className="mt-2 text-sm leading-5 text-ink-muted">
        {card.description}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onAction(card.action)}
          className="rounded border border-rule px-2.5 py-1 text-xs font-display uppercase tracking-wider text-ink transition-colors hover:bg-bg-card"
        >
          {actionLabel(card.action)}
        </button>
      </div>
    </article>
  );
}

function toneClass(tone: CoachFeedbackBoardCardTone): string {
  if (tone === "flag") return "border-flag/40";
  if (tone === "good") return "border-good/40";
  if (tone === "accent") return "border-accent/40";
  return "border-rule";
}

function actionLabel(action: CoachFeedbackBoardAction): string {
  if (action.kind === "roster") return "View player";
  if (action.kind === "engagement") return "Open follow-up";
  if (action.kind === "notes") return "Open notes";
  if (action.kind === "playlists") return "Open sessions";
  return "Open recaps";
}
